import React, { useState, useEffect } from 'react';
import { Select, Group, Text, Loader, Tooltip } from '@mantine/core';
import { IconRobot, IconChevronDown } from '@tabler/icons-react';
import { apiService } from '../services/apiService';
import { aiService } from '../services/aiService';
import { isAndroid, isWeb } from '../utils/environment';
import { useTranslation } from 'react-i18next';

interface AIConfig {
  id: number;
  name: string;
  provider: string;
  model: string;
  isCustom: boolean;
}

// Same rationale as aiService.ts's ANDROID_AI_SETTINGS_CACHE_KEY - the Android correction
// popup is a fresh WebView per open, so this component's own loadConfigurations() fetch
// (independent of aiService's) re-ran cold every time and sat on "Loading..." for the full
// network round-trip. Cache the resolved list/selection so a reopen renders instantly.
const ANDROID_AI_CONFIGS_CACHE_KEY = 'kalimat_android_ai_configs_cache';

interface AndroidAIConfigsCache {
  configs: AIConfig[];
  selectedConfigId: number | null;
  useCustomSettings: boolean;
}

function readAndroidAIConfigsCache(): AndroidAIConfigsCache | null {
  if (!isAndroid()) return null;
  try {
    const cached = localStorage.getItem(ANDROID_AI_CONFIGS_CACHE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
}

function writeAndroidAIConfigsCache(value: AndroidAIConfigsCache): void {
  if (!isAndroid()) return;
  try {
    localStorage.setItem(ANDROID_AI_CONFIGS_CACHE_KEY, JSON.stringify(value));
  } catch {
    // ignore cache write failures
  }
}

// Notifies same-window listeners immediately (useCurrentAIConfigName.ts, this component's
// own instance) AND cross-window listeners via Tauri's emit (the other AIModelSelector
// instance - main window vs correction-popup are separate OS windows/webviews on desktop, so
// the local dispatchEvent alone never reaches the other one). Mirrors the exact pattern
// App.tsx/CorrectionPopup.tsx/AIServiceManagement.tsx already use.
function dispatchAIConfigChanged(): void {
  window.dispatchEvent(new CustomEvent('ai-config-changed'));
  if (!isWeb()) {
    import('@tauri-apps/api/event')
      .then(({ emit }) => emit('ai-config-changed'))
      .catch(() => undefined);
  }
}

interface AIModelSelectorProps {
  onConfigChange?: (config: AIConfig | null) => void;
  compact?: boolean;
  disabled?: boolean;
  inline?: boolean; // New prop for inline header usage
}

export function AIModelSelector({ onConfigChange, compact = false, disabled = false, inline = false }: AIModelSelectorProps) {
  const { t } = useTranslation();
  const androidCache = readAndroidAIConfigsCache();
  const [configs, setConfigs] = useState<AIConfig[]>(androidCache?.configs ?? []);
  const [selectedConfigId, setSelectedConfigId] = useState<number | null>(androidCache?.selectedConfigId ?? null);
  const [isLoading, setIsLoading] = useState(androidCache === null);
  const [useCustomSettings, setUseCustomSettings] = useState(androidCache?.useCustomSettings ?? false);

  useEffect(() => {
    loadConfigurations();
    // Keep in sync with AIServiceManagement.tsx (the settings tab), which dispatches this
    // event on every create/update/delete/active-config change - without this listener this
    // selector only ever reflects whatever it saw on its own initial mount.
    const handleConfigChanged = () => {
      loadConfigurations();
    };
    window.addEventListener('ai-config-changed', handleConfigChanged);

    // The local DOM event above only reaches listeners in the SAME window/webview - it never
    // crosses the main window <-> correction-popup boundary (two separate OS windows on
    // desktop, each with their own AIModelSelector instance). Also listen for Tauri's
    // cross-window emit of the same event name (already emitted by App.tsx,
    // CorrectionPopup.tsx and AIServiceManagement.tsx), matching the pattern
    // useCurrentAIConfigName.ts already uses, so picking a model in one window updates the
    // other without needing a manual refresh.
    let unlistenPromise: Promise<() => void> | null = null;
    if (!isWeb()) {
      import('@tauri-apps/api/event')
        .then(({ listen }) => {
          unlistenPromise = listen('ai-config-changed', handleConfigChanged).then((unlisten) => unlisten);
        })
        .catch(() => undefined);
    }

    return () => {
      window.removeEventListener('ai-config-changed', handleConfigChanged);
      if (unlistenPromise) {
        unlistenPromise.then((unlisten) => unlisten()).catch(() => undefined);
      }
    };
  }, []);

  // Android already has cached data to render from androidCache above - only force the
  // "Loading..." state for this fetch if there's nothing cached to show in the meantime.
  const hadAndroidCache = androidCache !== null;

  const loadConfigurations = async () => {
    try {
      if (!hadAndroidCache) {
        setIsLoading(true);
      }
      const response = await apiService.getAISettings();

      const resolvedUseCustomSettings = response.useCustomSettings === true;
      setUseCustomSettings(resolvedUseCustomSettings);

      let resolvedConfigs: AIConfig[] = [];
      let resolvedSelectedConfigId: number | null = null;

      if (response.configurations && response.configurations.length > 0) {
        // Filter to show only custom configurations for user selection
        const customConfigs = response.configurations.filter((config: any) => config.isCustom === true);

        const configurations: AIConfig[] = customConfigs.map((config: any) => ({
          id: config.id,
          name: config.name || 'Custom Configuration',
          provider: config.provider || 'custom',
          model: config.model || 'unknown',
          isCustom: config.isCustom
        }));

        setConfigs(configurations);
        resolvedConfigs = configurations;

        // Only resolve a selected custom config when custom settings are actually active.
        // When useCustomSettings is false, the backend's activeConfigId points at a
        // default/non-custom config (filtered out of `configurations` above), so matching
        // it against this custom-only list always misses - that previously fell through to
        // "default to configurations[0]" even while custom mode was off, which is why the
        // selector appeared to silently jump to the first custom entry on every reload
        // instead of showing "Default AI" and remembering nothing the user actually picked.
        const activeConfigId = response.activeConfigId;
        if (resolvedUseCustomSettings && activeConfigId && configurations.find(c => c.id === activeConfigId)) {
          setSelectedConfigId(activeConfigId);
          resolvedSelectedConfigId = activeConfigId;
          const activeConfig = configurations.find(c => c.id === activeConfigId);
          if (onConfigChange && activeConfig) {
            onConfigChange(activeConfig);
          }
        } else if (resolvedUseCustomSettings && configurations.length > 0) {
          // Custom mode is on but the previously active id wasn't found among current
          // custom configs (e.g. it was deleted) - fall back to the first one rather than
          // showing nothing.
          setSelectedConfigId(configurations[0].id);
          resolvedSelectedConfigId = configurations[0].id;
          if (onConfigChange) {
            onConfigChange(configurations[0]);
          }
        } else {
          // Custom settings are off - selection is "Default AI", not any custom config.
          setSelectedConfigId(null);
          resolvedSelectedConfigId = null;
          if (onConfigChange) {
            onConfigChange(null);
          }
        }
      }

      // Cache regardless of whether any custom configs exist - the far more common "no
      // custom config" case needs to be instantly restorable too, same reasoning as
      // aiService.ts's cacheForAndroid().
      writeAndroidAIConfigsCache({
        configs: resolvedConfigs,
        selectedConfigId: resolvedSelectedConfigId,
        useCustomSettings: resolvedUseCustomSettings,
      });
    } catch (error) {
      console.error('Error loading AI configurations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfigChange = async (value: string | null) => {
    if (!value) return;
    
    const configId = parseInt(value);
    setSelectedConfigId(configId);
    
    const config = configs.find(c => c.id === configId);
    if (config) {
      try {
        // Set as active configuration in backend
        await apiService.setActiveAIConfiguration(configId);
        // Re-initialize AI service
        await aiService.initializeWithAISettingsFromBackend();
        // Notify other components
        dispatchAIConfigChanged();

        if (onConfigChange) {
          onConfigChange(config);
        }
      } catch (error) {
        console.error('Error switching AI configuration:', error);
      }
    }
  };

  const selectData = configs.map(config => ({
    value: config.id.toString(),
    label: config.name,
    description: `${config.model} (${config.provider})`
  }));

  // Add default option
  const allOptions = [
    { value: 'default', label: t('settings.aiServices.defaultConfig') || 'Default AI', description: t('settings.aiServices.defaultConfigDesc') || 'Use default AI service' },
    ...selectData
  ];

  if (isLoading) {
    return inline ? (
      <Group gap="xs">
        <Loader size="xs" />
        <Text size="xs" c="dimmed">{t('common.loading') || 'Loading...'}</Text>
      </Group>
    ) : (
      <Group gap="xs">
        <Loader size="xs" />
        <Text size="xs" c="dimmed">{t('common.loading') || 'Loading...'}</Text>
      </Group>
    );
  }

  if (configs.length === 0 && !useCustomSettings) {
    return inline ? (
      <Group gap="xs">
        <IconRobot size={14} />
        <Text size="xs" c="dimmed">{t('settings.aiServices.defaultAI') || 'Default AI'}</Text>
      </Group>
    ) : (
      <Tooltip label={t('settings.aiServices.noCustomConfigs') || 'No custom AI configurations available'}>
        <Group gap="xs">
          <IconRobot size={16} />
          <Text size="xs" c="dimmed">{t('settings.aiServices.defaultAI') || 'Default AI'}</Text>
        </Group>
      </Tooltip>
    );
  }

  if (inline) {
    // Inline mode for header - show as styled text that opens dropdown on click
    const currentConfig = configs.find(c => c.id === selectedConfigId);
    currentConfig
        ? (currentConfig.model || currentConfig.name)
        : (t('settings.aiServices.defaultAI') || 'Default AI');
    return (
        <Select
        size="xs"
        placeholder={t('settings.aiServices.selectModel') || 'Select AI Model'}
        data={allOptions}
        value={selectedConfigId?.toString() || (useCustomSettings ? undefined : 'default')}
        onChange={async (value) => {
          if (value === 'default') {
            try {
              await apiService.setActiveAIConfiguration(0);
              await aiService.initializeWithAISettingsFromBackend();
              setSelectedConfigId(null);
              setUseCustomSettings(false);
              dispatchAIConfigChanged();
              if (onConfigChange) {
                onConfigChange(null);
              }
            } catch (error) {
              console.error('Error switching to default AI:', error);
            }
          } else if (value) {
            await handleConfigChange(value);
          }
        }}
        disabled={disabled}
        leftSection={<IconRobot size={14} color="#6366f1" />}
        rightSection={<IconChevronDown size={12} />}
        styles={{
          input: {
            minWidth: '120px',
            maxWidth: '150px',
            fontSize: '12px',
            padding: '4px 24px 4px 22px', // Further reduced left padding
          },
          section: {
            width: '20px',
            marginRight: '2px',
          }
        }}
      />
    );
  }

  return (
    <Select
      size={compact ? 'xs' : 'sm'}
      placeholder={t('settings.aiServices.selectModel') || 'Select AI Model'}
      data={allOptions}
      value={selectedConfigId?.toString() || (useCustomSettings ? undefined : 'default')}
      onChange={async (value) => {
        if (value === 'default') {
          // Switch to default
          try {
            await apiService.setActiveAIConfiguration(0);
            await aiService.initializeWithAISettingsFromBackend();
            setSelectedConfigId(null);
            setUseCustomSettings(false);
            dispatchAIConfigChanged();
            if (onConfigChange) {
              onConfigChange(null);
            }
          } catch (error) {
            console.error('Error switching to default AI:', error);
          }
        } else if (value) {
          await handleConfigChange(value);
        }
      }}
      disabled={disabled}
      leftSection={<IconRobot size={16} />}
      styles={{
        input: {
          minWidth: compact ? '150px' : '200px',
        }
      }}
    />
  );
}
